<template>
  <aside class="sidebar" @mouseleave="closeAllMenus">
    <div class="sidebar-container">
      <!-- 사이드바 메뉴 목록 -->
      <ul class="menu-list">
        <template v-for="(section, index) in visibleMenuSections" :key="index">
          <li
            class="menu-item"
            :class="{
              'has-submenu': section.submenus && section.submenus.length > 0,
              'menu-item--no-submenu': !section.submenus || section.submenus.length === 0,
              'menu-item--last': index === visibleMenuSections.length - 1,
              'active-menu': activeIndex === index,
            }"
            @mouseenter="hoverMenu(index)"
            @mouseleave="hoveringIndex = null"
            @click="clickMenu(index, $event)"
          >
            <!-- 메인 메뉴 아이템 -->
            <div class="menu-link" :class="{ active: activeIndex === index }">
              <div class="menu-icon-container">
                <div class="menu-icon">
                  <img :src="getMenuIconSrc(section.icon, hoveringIndex === index || activeIndex === index)" alt="icon" />
                </div>
                <div class="menu-text">{{ section.title }}</div>
              </div>
            </div>

            <!-- 서브메뉴 섹션 -->
            <div
              v-show="hoveringIndex === index || (isExpanded && activeIndex === index)"
              class="submenu-sections"
              :class="{ 'pinned-submenu': isExpanded && index === activeIndex }"
              @click.stop
            >
              <!-- 대메뉴명(L2) + 핀 버튼 -->
              <div class="submenu-sections-header">
                <span class="submenu-sections-header-title">{{ section.title }}</span>
                <div class="pin-button-container">
                  <button class="pin-button" :class="{ pinned: isExpanded }" @click.stop="togglePinned" title="사이드바 고정">
                    <i v-if="!isExpanded" class="icon-md icon-pin"></i>
                    <i v-else class="icon-md icon-pin-pinned"></i>
                  </button>
                </div>
              </div>

              <!-- 대메뉴 전용 팝업 메뉴 (메뉴 이동이 아닌 팝업 오픈 전용이지만, 서브메뉴와 같은 리스트 형태로 노출. 선택 상태 표시는 없음) -->
              <div v-if="getPopupMenu(section)" class="submenu-popup-section">
                <button
                  v-for="popupButton in getPopupMenu(section).buttons"
                  :key="popupButton.key"
                  type="button"
                  class="submenu-popup-link"
                  @click="popupButton.onClick"
                >
                  <span class="submenu-popup-link-text">{{ popupButton.label }}</span>
                  <span v-if="popupButton.badge" class="submenu-popup-link-badge">{{ popupButton.badge }}</span>
                  <span v-if="popupButton.external" class="submenu-popup-link-external" aria-hidden="true">↗</span>
                </button>
              </div>

              <!-- 서브메뉴 섹션들 -->
              <div
                v-for="(submenu, submenuIndex) in section.submenus"
                :key="submenuIndex"
                class="submenu-section"
                :class="{
                  'submenu-section--title-only': submenu.navigateByTitleOnly,
                  'submenu-section--first-with-children': submenuIndex === 0 && !submenu.navigateByTitleOnly,
                  'submenu-section--has-visible-top-border': submenuSectionShowsBorderTop(section.submenus, submenuIndex),
                }"
              >
                <div
                  class="submenu-section-title"
                  :class="{
                    clickable: isSubmenuTitleClickable(submenu),
                    'submenu-section-title--disabled': !isSubmenuTitleClickable(submenu),
                    'submenu-section-title--active': isTitleOnlySubmenuActive(submenu),
                  }"
                  @click="handleSubmenuTitleClick(submenu, $event)"
                >
                  <span class="section-indicator">›</span>
                  <span class="submenu-section-title-text">{{ submenu.title }}</span>
                </div>
                <ul v-show="submenu.items?.length && !submenu.navigateByTitleOnly" class="nested-submenu">
                  <li v-for="(submenuItem, submenuItemIndex) in submenu.items" :key="submenuItemIndex" class="nested-submenu-item">
                    <NuxtLink
                      :to="getMenuLinkTo(submenuItem)"
                      class="nested-submenu-link"
                      :class="{
                        'active-with-tabs': isMenuItemActive(submenuItem),
                        disabled: submenuItem.disabled,
                      }"
                      :key="submenuItem.path"
                      @click="event => handleMenuClick(submenuItem, event)"
                    >
                      <div class="menu-text">{{ submenuItem.name }}</div>
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </template>
      </ul>
    </div>
  </aside>

  <!-- SAI 모달 -->
  <UiModal v-model="isSaiModalOpen" position="right" title="S-AI" size="medium" :isScroll="false" :allowBackgroundInteraction="true">
    <div class="sai-iframe-container">
      <iframe v-if="isSaiModalOpen" :src="saiUrl" class="sai-iframe" frameborder="0" allow="microphone; camera" />
    </div>
  </UiModal>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMenuStore } from '~/stores/useMenuStore';
import { useUserStore } from '~/composables/user/useUserStore';
import { useAuthStore } from '~/stores/useAuthStore';
import { useNavigateToAsis } from '~/composables/useNavigateToAsis';
import { navigateWithAppLocale } from '~/composables/useAppLocale';

const emit = defineEmits(['toggle-expanded']);
const { navigateToAsis } = useNavigateToAsis();

const config = useRuntimeConfig();
const appBase = (config.app?.baseURL || '/').replace(/\/$/, '') || '';
/** ASIS→TOBE 전환 시 baseURL 미반영으로 깨지지 않도록 아이콘 경로 생성 */
function getMenuIconSrc(iconName, isHover) {
  if (!iconName) return `${appBase}/images/default-icon.svg`;
  if (iconName.startsWith('http') || (iconName.startsWith('/') && iconName.includes('/'))) {
    return iconName;
  }
  const base = appBase ? `${appBase}/images` : '/images';
  return `${base}/${iconName}${isHover ? '-hover' : ''}.svg`;
}

// useUserStore에서 betaFeatures 가져오기
const { betaFeatures } = useUserStore();

const route = useRoute();
const authStore = useAuthStore();
const saiChatStore = useSaiChatStore();
const samePathRefreshKey = useState('same-path-refresh-key', () => 0);

const getSidebarPinnedStorageKey = (compId, userId) => `sgate.sidebarPinned.${compId}.${userId}`;

const sidebarPinnedStorageKey = computed(() => {
  const compId = authStore.user?.compId;
  const userId = authStore.user?.userId;
  if (!compId || !userId) return null;
  return getSidebarPinnedStorageKey(compId, userId);
});

const readSidebarPinnedFromStorage = key => {
  if (!key || !process.client) return null;
  let stored = null;
  try {
    stored = localStorage.getItem(key);
    if (stored === null) {
      stored = sessionStorage.getItem(key);
    }
  } catch (error) {
    stored = null;
  }
  if (stored === 'true' || stored === 'false') {
    return stored === 'true';
  }
  return null;
};

const persistSidebarPinnedState = (key, pinned) => {
  if (!key || !process.client) return;
  const value = pinned ? 'true' : 'false';
  try {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch (error) {
    // ignore storage access errors (e.g. private mode)
  }
};

// 활성화된 메뉴 인덱스 추적
const activeIndex = ref(null);
// 호버 상태 추적
const hoveringIndex = ref(null);

// 레이아웃에서 확장 상태 공유
const isExpanded = inject('isExpanded', ref(false));

/** sidebar.scss .submenu-section border 규칙과 동일 — 실제로 border-top이 그려지는 블록 */
function submenuSectionShowsBorderTop(subs, i) {
  const s = subs[i];
  if (!s || s.navigateByTitleOnly) return false;
  if (i === 0) return false;
  const prev = subs[i - 1];
  if (!prev.navigateByTitleOnly) return false;
  return true;
}

// 메뉴 호버 함수 - 핀 고정 상태와 관계없이 항상 호버 효과 활성화
function hoverMenu(index) {
  hoveringIndex.value = index;
}

/** 대메뉴(section)에서 첫 번째 이동 가능한 소메뉴 */
function getFirstNavigableItem(section) {
  if (!section?.submenus?.length) return null;

  for (const submenu of section.submenus) {
    if (!submenu.items?.length) continue;
    const item = submenu.items.find(i => !i.disabled && i.path && i.path !== '#');
    if (item) return item;
  }
  return null;
}

// 메뉴 클릭 함수 - 첫 번째 소메뉴로 이동
function clickMenu(index, event) {
  activeIndex.value = index;

  const section = visibleMenuSections.value[index];
  const item = getFirstNavigableItem(section);
  if (item) {
    handleMenuClick(item, event);
  }
}

// 핀 토글 함수 - 사이드바 전체 확장 상태만 토글
function togglePinned() {
  // 확장 상태 토글
  isExpanded.value = !isExpanded.value;
  persistSidebarPinnedState(sidebarPinnedStorageKey.value, isExpanded.value);

  emit('toggle-expanded');
}

// useMenuStore 사용
const menuStore = useMenuStore();
const menuSections = computed(() => menuStore.sections);

// 대메뉴(pgmId) 전용 팝업 메뉴 그룹 — 메뉴 이동 없이 팝업만 여는 버튼이지만, 기존 서브메뉴와 같은 메뉴 리스트 형태로 노출. 필요한 대메뉴에만 추가.
// TheHeader.vue의 SAI 아이콘과 동일한 클릭 이벤트
const handleClickSaiAskButton = () => {
  saiChatStore.openSaiModal();
};

const openManualPopup = () => {
  const manualUrl = 'https://strategygate.notion.site/Sgate-11274c51396980c1b8adf56262517440?pvs=4';
  window.open(manualUrl, '_blank');
};

const openFaqPopup = () => {
  const faqUrl = 'https://strategygate.notion.site/Sgate-Ver-3ec74c513969800483dbe34551c35432';
  window.open(faqUrl, '_blank');
};

const POPUP_MENU_BY_PGM_ID = {
  PGM1765: {
    buttons: [
      { key: 'sai-ask', label: 'S-ai에게 물어보기', badge: 'Beta', onClick: handleClickSaiAskButton },
      { key: 'manual', label: '매뉴얼', external: true, onClick: openManualPopup },
      { key: 'faq', label: '자주 묻는 질문', external: true, onClick: openFaqPopup },
    ],
  },
};

const getPopupMenu = section => POPUP_MENU_BY_PGM_ID[section.pgmId] || null;

/** compId별 사이드바 대메뉴 노출 조정 */
const visibleMenuSections = computed(() => {
  let sections = menuSections.value;
  const compId = authStore.user?.compId;

  if (compId === 'isparkpa') {
    sections = sections.filter(section => section.title !== '대시보드');
  }

  return sections;
});

// 메뉴 섹션 디버깅
/*
watch(menuSections, (newSections) => {
  console.log('TheSidebar - menuSections 변경됨:', newSections);
  console.log('TheSidebar - menuSections 길이:', newSections.length);
}, { immediate: true, deep: true });
*/
// 현재 경로에 따라 활성화된 메뉴 자동 설정
const initActiveMenu = () => {
  const currentPath = route.path;
  let bestMatch = null;
  let maxMatchLength = 0;

  visibleMenuSections.value.forEach((section, index) => {
    if (!section.submenus) return;

    // 서브메뉴 아이템들을 확인하여 가장 긴 경로 매칭 찾기
    section.submenus.forEach(submenu => {
      if (!submenu.items) return;

      submenu.items.forEach(item => {
        if (!item.path) return;

        let matchLength = 0;
        let isMatch = false;

        // 1. 정확한 경로 일치 확인
        if (currentPath === item.path) {
          matchLength = item.path.length;
          isMatch = true;
        }
        // 2. 경로가 prefix인지 확인 (더 정교한 매칭)
        else if (currentPath.startsWith(item.path)) {
          const nextChar = currentPath[item.path.length];
          // 경로가 정확히 일치하거나, 다음 문자가 '/'이거나 없어야 함
          if (!nextChar || nextChar === '/') {
            matchLength = item.path.length;
            isMatch = true;
          }
        }

        // 3. 탭 경로도 확인
        if (!isMatch && item.tabs && Array.isArray(item.tabs)) {
          for (const tab of item.tabs) {
            if (tab.path) {
              // 탭 경로는 정확히 일치해야 함
              if (currentPath === tab.path) {
                matchLength = tab.path.length;
                isMatch = true;
                break;
              }
              // 탭 경로가 현재 경로의 시작 부분과 일치하는 경우
              if (currentPath.startsWith(tab.path)) {
                const nextChar = currentPath[tab.path.length];
                if (!nextChar || nextChar === '/') {
                  matchLength = tab.path.length;
                  isMatch = true;
                  break;
                }
              }
            }
          }
        }

        // 가장 긴 매칭을 찾아서 저장
        if (isMatch && matchLength > maxMatchLength) {
          maxMatchLength = matchLength;
          bestMatch = index;
        }
      });
    });
  });

  // 가장 잘 매칭된 메뉴 인덱스 설정
  if (bestMatch !== null) {
    activeIndex.value = bestMatch;
  }
};

// 라우트 변경 감지
watch(
  () => route.path,
  () => {
    // 활성 메뉴 업데이트
    initActiveMenu();
  },
);

// 메뉴 섹션이 로드된 후에도 활성 메뉴 업데이트 (메뉴가 늦게 로드되는 경우 대비)
watch(
  () => visibleMenuSections.value.length,
  (newLength) => {
    if (newLength > 0) {
      // 메뉴가 로드된 후 활성 메뉴 업데이트
      nextTick(() => {
        initActiveMenu();
      });
    }
  },
);

// 컴포넌트 마운트 시 초기화
onMounted(async () => {
  // 활성 메뉴 초기화
  initActiveMenu();
});

const hasRestoredPinnedState = ref(false);
watch(
  sidebarPinnedStorageKey,
  key => {
    if (hasRestoredPinnedState.value || !key) return;
    const restored = readSidebarPinnedFromStorage(key);
    if (restored !== null) {
      isExpanded.value = restored;
    }
    hasRestoredPinnedState.value = true;
  },
  { immediate: true },
);

// 모든 메뉴 닫기 함수 - 핀 고정 상태와 관계없이 호버링 상태만 초기화
const closeAllMenus = () => {
  hoveringIndex.value = null;
};

// 클릭 처리 중인지 추적
const isNavigating = ref(false);

// 메뉴 아이템이 현재 활성 상태인지 확인 (탭 경로 포함)
const isMenuItemActive = menuItem => {
  const currentPath = route.path;

  // 1. 정확한 경로 일치를 먼저 확인
  if (menuItem.path && currentPath === menuItem.path) {
    return true;
  }

  // 2. 메뉴 아이템의 기본 경로가 현재 경로의 prefix인지 확인
  // 단, 경로 뒤에 추가 문자가 없거나 '/'인 경우만 매칭
  if (menuItem.path && currentPath.startsWith(menuItem.path)) {
    const nextChar = currentPath[menuItem.path.length];
    // 경로가 정확히 일치하거나, 다음 문자가 '/'이거나 없어야 함
    // 예: '/screens/org-evaluation/org-eval' vs '/screens/org-evaluation/org-eval-etc-item'
    //     → nextChar가 '-'이므로 매칭하지 않음
    if (!nextChar || nextChar === '/') {
      return true;
    }
  }

  // 3. 메뉴 아이템에 탭이 있는 경우, 탭 경로들도 확인
  if (menuItem.tabs && Array.isArray(menuItem.tabs)) {
    for (const tab of menuItem.tabs) {
      // 탭 경로는 정확히 일치해야 함
      if (tab.path && currentPath === tab.path) {
        return true;
      }
      // 탭 경로가 현재 경로의 시작 부분과 일치하는 경우
      // 단, 경로 뒤에 추가 문자가 없거나 '/'인 경우만 매칭
      if (tab.path && currentPath.startsWith(tab.path)) {
        const nextChar = currentPath[tab.path.length];
        if (!nextChar || nextChar === '/') {
          return true;
        }
      }
    }
  }

  return false;
};

/** viewType 'A'(AS-IS)인 경우 Nuxt 라우트로 가지 않도록 to="#" 반환 */
const getMenuLinkTo = item => {
  if (item?.disabled) return '#';
  if (item?.viewType === 'A') return '#';
  return item?.path || '#';
};
/** L4 ul 없이 제목만 있는 L3 — 현재 경로가 그 항목이면 L4 링크와 동일한 선택 표시 */
const isTitleOnlySubmenuActive = submenu => {
  if (!submenu?.navigateByTitleOnly || !submenu?.items?.length) return false;
  return submenu.items.some(item => isMenuItemActive(item));
};

/** L3 제목 클릭으로 이동 가능 — 활성 URL이 하나라도 있는 항목이 있을 때만 */
const isSubmenuTitleClickable = submenu =>
  !!(submenu?.items?.length && submenu.items.some(item => !item.disabled));

// 서브메뉴 타이틀 클릭 핸들러 - 첫 번째 항목 클릭 효과
const handleSubmenuTitleClick = (submenu, event) => {
  // 이벤트 전파 방지
  event?.stopPropagation();

  // submenu.items에서 첫 번째 disabled가 아닌 항목 찾기
  if (!submenu.items || submenu.items.length === 0) {
    return;
  }

  const firstEnabledItem = submenu.items.find(item => !item.disabled);

  if (!firstEnabledItem || !firstEnabledItem.path) {
    return;
  }

  // 첫 번째 항목을 클릭한 것과 동일한 효과
  handleMenuClick(firstEnabledItem, event);
};

const { logTabMenuAccess } = useTabNavigation();

/** 탭이 없는 메뉴 클릭 로그. 탭 메뉴는 각 화면에서 탭 ID로 기록한다. */
const logMenuAccessWithoutTabs = menuItem => {
  if (!menuItem || menuItem.tabs?.length) return;
  logTabMenuAccess('', menuItem.pgmId || '');
};

// 메뉴 클릭 핸들러 - viewType에 따라 AS-IS(풀리로드) / TO-BE(SPA) 분기
const handleMenuClick = async (submenuItem, event) => {
  const path = submenuItem?.path;
  const disabled = submenuItem?.disabled ?? false;
  const viewType = submenuItem?.viewType;

  // NuxtLink/라우터 간섭 방지: 항상 먼저 기본 동작·전파 차단
  event?.preventDefault();
  event?.stopPropagation();

  // disabled 상태면 클릭 무시
  if (disabled) {
    if (import.meta.dev) console.log('⚠️ [TheSidebar] Disabled menu clicked, ignoring');
    return;
  }

  // viewType === 'A' → AS-IS: 쿼리 없이 entryPath + hash만 사용 (예: /main.do#PGM1722)
  if (viewType === 'A') {
    const hash = submenuItem?.pgmId || submenuItem?.menuId || '';
    if (import.meta.dev) {
      // eslint-disable-next-line no-console
      console.log('🔀 [TheSidebar] viewType=A → navigateToAsis (Full reload)', { hash });
    }
    navigateToAsis({
      entryPath: '/main.do',
      hash,
    });
    return;
  }

  if (isNavigating.value) {
    console.log('⏳ TheSidebar - 네비게이션 진행 중, 클릭 무시');
    return;
  }

  isNavigating.value = true;
  authStore.refreshUnreadNotificationCnt();

  try {
    console.log('🚀 TheSidebar - 네비게이션 시작:', path);
    const isSamePath = route.path === path;

    if (isSamePath) {
      // 동일 메뉴 재클릭은 라우트 미들웨어를 타지 않으므로 세션 체크를 직접 수행
      const isValid = await authStore.checkAuth();
      if (!isValid) return;

      // 동일 메뉴 재클릭: NuxtPage key 갱신으로 SPA 재마운트
      samePathRefreshKey.value += 1;
      logMenuAccessWithoutTabs(submenuItem);
      return;
    }

    await navigateWithAppLocale(path);
    logMenuAccessWithoutTabs(submenuItem);
    console.log('✅ TheSidebar - 네비게이션 완료:', path);
  } catch (error) {
    console.error('❌ TheSidebar - 네비게이션 에러:', error);
  } finally {
    setTimeout(() => {
      isNavigating.value = false;
    }, 300);
  }
};

// SAI 모달 상태
const isSaiModalOpen = ref(false);
const saiUrl = ref('');

// 환경변수에서 SAI 기본 URL 가져오기
const runtimeConfig = useRuntimeConfig();
const saiBaseUrl = runtimeConfig.public.saiUrl;

// SAI 모달 열기 함수
const openSaiModal = async () => {
  try {
    // API를 통해 토큰 조회
    const response = await useApi('/api/common/apiToken.do', {
      method: 'POST',
    });

    const token = response.token;
    saiUrl.value = `${saiBaseUrl}?token=${token}`;
    isSaiModalOpen.value = true;
  } catch (error) {
    console.error('SAI 토큰 조회 실패:', error);
  }
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/sidebar.scss';

.sai-iframe-container {
  width: 100%;
  height: calc(100vh - 90px);

  .sai-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
}
:deep(.ui-popup__body) {
  padding: 0px !important;
}

/* 서브 패널 상단: 대메뉴명(L2, 좌) + 핀(우) — sidebar.scss .pin-button-container absolute는 헤더 안에서 해제 */
.submenu-sections-header {
  position: absolute;
  top: 10px;
  left: 8px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  z-index: 10002;
}

.submenu-sections-header-title {
  color: var(--color-gray-30, #b1b8be);
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  margin-bottom: 0;
  padding-left: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submenu-sections-header .pin-button-container {
  position: static;
  flex-shrink: 0;
}

/* NuxtLink 루트가 자식 컴포넌트라 scoped 시 sidebar.scss 선택자가 안 맞는 경우 대비 — 활성: 글씨 primary + .menu-text::before 삼각 아이콘 */
:deep(.nested-submenu-link.router-link-active),
:deep(.nested-submenu-link.active-with-tabs) {
  color: var(--primary-color, #00aaff);
}

:deep(.nested-submenu-link.router-link-active .menu-text),
:deep(.nested-submenu-link.active-with-tabs .menu-text) {
  position: relative;
  color: var(--primary-color, #00aaff);
  font-weight: 700; /* 활성 항목은 굵게 — 일반(500)과 구분 */
  padding-left: 12px;
}

:deep(.nested-submenu-link.router-link-active .menu-text::before),
:deep(.nested-submenu-link.active-with-tabs .menu-text::before) {
  content: "";
  position: absolute;
  top: 48%;
  left: 0;
  width: 8px;
  height: 8px;
  transform: translateY(-50%);
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='8' fill='none' viewBox='0 0 7 8'%3E%3Cpath fill='%230AF' d='M0 1.618A1 1 0 0 1 1.447.724l4.764 2.382a1 1 0 0 1 0 1.788L1.447 7.276A1 1 0 0 1 0 6.382z'/%3E%3C/svg%3E");
  background-position: 50%;
  background-size: cover;
}

/* title-only L3 — 삼각은 메뉴명 span에만 (L4 .menu-text와 동일: padding-left 12px + ::before left 0) */
.submenu-section-title.submenu-section-title--active {
  color: var(--primary-color, #00aaff);
}

.submenu-section-title.submenu-section-title--active .submenu-section-title-text {
  position: relative;
  color: var(--primary-color, #00aaff);
  padding-left: 12px;
}

.submenu-section-title.submenu-section-title--active .submenu-section-title-text::before {
  content: "";
  position: absolute;
  top: 48%;
  left: 0;
  width: 8px;
  height: 8px;
  transform: translateY(-50%);
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='8' fill='none' viewBox='0 0 7 8'%3E%3Cpath fill='%230AF' d='M0 1.618A1 1 0 0 1 1.447.724l4.764 2.382a1 1 0 0 1 0 1.788L1.447 7.276A1 1 0 0 1 0 6.382z'/%3E%3C/svg%3E");
  background-position: 50%;
  background-size: cover;
}

.submenu-section--title-only .submenu-section-title.submenu-section-title--active {
  color: var(--primary-color, #00aaff);
}

.submenu-section--title-only .submenu-section-title.submenu-section-title--active .submenu-section-title-text {
  color: var(--primary-color, #00aaff);
}

.submenu-section-title.submenu-section-title--active.clickable:hover .submenu-section-title-text {
  color: var(--primary-color, #00aaff);
}

/* 대메뉴 전용 팝업 메뉴 — 기존 .submenu-section/.nested-submenu-link와 같은 메뉴 리스트 모양. 단, path/active 로직과는 무관해 선택 상태 표시는 없다.
   아래 실제 메뉴와 가로 구분선이 생기지 않도록 border/여백은 두지 않는다. */
.submenu-popup-section {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.submenu-popup-section + .submenu-section {
  border-top: none;
  margin-top: 0;
}

.submenu-popup-link {
  display: flex;
  align-items: center;
  width: 100%;
  text-decoration: none;
  white-space: normal;
  transition: all 0.2s;
  border: none;
  border-radius: 4px;
  background: transparent;
  padding: 6px 8px;
  margin-bottom: 4px;
  cursor: pointer;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-70, #464c53);
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
}

.submenu-popup-link:hover {
  background: var(--color-primary-10, #cef);
  color: var(--primary-color, #00aaff);
}

.submenu-popup-link-text {
  flex: 1;
  min-width: 0;
}

.submenu-popup-link-badge {
  flex-shrink: 0;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--color-primary-10, #cef);
  color: var(--primary-color, #00aaff);
  font-size: 10px;
  font-weight: 700;
}

.submenu-popup-link-external {
  flex-shrink: 0;
  margin-left: 6px;
  color: var(--color-gray-30, #b1b8be);
  font-size: 12px;
}
</style>
